import React from 'react';
import ReactDOM from 'react-dom';
import { Grid, Divider } from '@mui/material';
import UserListToolbar from './UserListToolbar';
import EntityExportDialog from '../components/EntityExportDialog';
import ExportButton from '../components/ExportButton';
import RecordListPro from '../components/RecordListPro';
import SelectAllBanner from '../components/SelectAllBanner';
import useGridExport from '../components/useGridExport';
import useSelectAll from '../components/useSelectAll';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme) => ({
    tabPanelWrapper: {
        height: '100%',
        borderLeft: '1px solid #d8d8d8',
        borderRight: '1px solid #d8d8d8',
        [theme.breakpoints.down('sm')]: {
            border: 'none !important',
            padding: '1rem',
        },
    },
}));

const UserListTab = ({
    isGuest,
    recordListRoleFilters,
    setRecordListRoleFilters,
    hasActiveFutureBooking,
    setHasActiveFutureBooking,
    currentTab,
    resource,
    onQueryChange,
    onDeleteUser,
    isSmallScreen,
    query,
    apiRef,
    lastUpdate,
    syncActive,
    // The page header's actions slot (UserNavigateToolbar `actionsRef`), or
    // null when the user may not export. The Export button renders there,
    // beside [+ New user] (CP-8291), but its state stays here with the grid.
    headerActionsEl,
    ...props
}) => {
    const classes = useStyles();

    // Memoized because RecordListPro keys its data-fetch effect on these props.
    // Built inline they are new array identities on every render, so any state
    // that lives above this component (row selection) would refetch the grid on
    // every change and wipe the selection with it -- see aidocs/lessons.md #4,
    // which recorded exactly this failure in TripList.
    const apiFilters = React.useMemo(() => (isGuest
        ? [
            { field: 'regular_users', op: 'null', value: true },
            { field: 'fake_users', value: false },
            ...(hasActiveFutureBooking ? [{ field: 'has_active_future_booking', value: true }] : []),
          ]
        : [{ field: 'regular_users', op: 'null', value: false }]
    ), [isGuest, hasActiveFutureBooking]);

    const sortModel = React.useMemo(
        () => [{ field: isGuest ? 'first_name,last_name,email' : 'first_name', sort: 'asc' }],
        [isGuest],
    );

    // `include_fake` is a bare query param, outside the `filters` JSON, so it
    // has to be replayed explicitly onto select_all_ids and export or those two
    // resolve a different row set than the grid shows.
    const extraParams = React.useMemo(() => ({ include_fake: true }), []);

    // The filters the grid is ACTUALLY using: its interactive column filters
    // plus the tab's static ones. RecordListPro owns the column filters, so
    // without this the toolbar only ever saw `apiFilters` -- a grid filtered
    // down to 24,620 rows still reported, and exported, all 735,530.
    const [gridFilters, setGridFilters] = React.useState(apiFilters);
    React.useEffect(() => {
        setGridFilters(apiFilters);
    }, [apiFilters]);

    // Selection lives here rather than in UserList: the tabs are conditionally
    // rendered, so switching tabs unmounts this component and clears the
    // selection on its own.
    const {
        selectedIds, setSelectedIds, selectAll, deselectAll, selectAllMeta,
        loading: selectAllLoading, isAllSelected,
    } = useSelectAll({
        resource, apiFilters: gridFilters, query, extraParams,
        // The only bulk action on this grid is export, which sends the filters
        // rather than an id list — so select_all_ids' 5,000-id cap bounds
        // nothing here and must not be reported as a limit. Revisit if an
        // id-based bulk action (batch delete) is ever wired up.
        allMatchingCapable: true,
    });

    // XML/JSON entity export: same filter/selection plumbing as the grid
    // export, but through the options dialog (format / hints / import source).
    // The dialog opens on the format whose menu item was clicked.
    const [documentExportOpen, setDocumentExportOpen] = React.useState(false);
    const [documentExportFormat, setDocumentExportFormat] = React.useState(null);
    const openDocumentExport = React.useCallback((format) => {
        setDocumentExportFormat(typeof format === 'string' ? format : null);
        setDocumentExportOpen(true);
    }, []);
    const { exportDocument, exporting: documentExporting } = useGridExport({
        resource,
        apiFilters: gridFilters,
        query,
        extraParams,
        urlSuffix: '?include_fake=true',
        selectedIds,
        isAllSelected,
    });

    // The grid only knows about the rows on the current page, so its own
    // footer count would understate a cross-page selection.
    const localeText = React.useMemo(() => ({
        footerRowSelected: () => (selectedIds.length === 1
            ? '1 row selected'
            : `${selectedIds.length.toLocaleString()} rows selected`),
    }), [selectedIds.length]);

    return (
        <>
            {headerActionsEl && ReactDOM.createPortal(
                <ExportButton
                    resource={resource}
                    apiFilters={gridFilters}
                    query={query}
                    extraParams={extraParams}
                    selectedIds={selectedIds}
                    isAllSelected={isAllSelected}
                    matchingCount={selectAllMeta?.totalCount}
                    filenameFallback={isGuest ? 'guests' : 'staff'}
                    documentFormats={['xml', 'json']}
                    onDocumentFormat={openDocumentExport}
                    size={isSmallScreen ? 'medium' : 'small'}
                    hideLabel={isSmallScreen}
                />,
                headerActionsEl,
            )}
            <Grid container direction={'row'} className={classes.tabPanelWrapper}>
                <Grid item xs={12}>
                    <UserListToolbar
                        recordListRoleFilters={recordListRoleFilters}
                        setRecordListRoleFilters={setRecordListRoleFilters}
                        hasActiveFutureBooking={hasActiveFutureBooking}
                        setHasActiveFutureBooking={setHasActiveFutureBooking}
                        isGuest={isGuest}
                        currentTab={currentTab}
                        resource={resource}
                        onQueryChange={onQueryChange}
                        onDeleteUser={onDeleteUser}
                        isSmallScreen={isSmallScreen}
                        query={query}
                    />
                    <EntityExportDialog
                        open={documentExportOpen}
                        onClose={() => setDocumentExportOpen(false)}
                        entity="user"
                        initialFormat={documentExportFormat}
                        exportDocument={exportDocument}
                        exporting={documentExporting}
                        selectionLabel={isAllSelected || !selectedIds.length
                            ? 'all matching users'
                            : `${selectedIds.length.toLocaleString()} selected users`}
                    />
                    {!isSmallScreen && <Divider />}
                </Grid>
                <Grid item xs={true} style={{ marginTop: '-25px', position: 'relative' }}>
                    <SelectAllBanner
                        selectedIds={selectedIds}
                        selectAllMeta={selectAllMeta}
                        onSelectAll={selectAll}
                        onDeselectAll={deselectAll}
                        loading={selectAllLoading}
                    />
                    <RecordListPro
                        {...props}
                        onDeleteUser={onDeleteUser}
                        apiRef={apiRef}
                        resource={resource}
                        urlSuffix={'?include_fake=true'}
                        apiFilters={apiFilters}
                        onEffectiveFiltersChange={setGridFilters}
                        disableSelectionOnClick={true}
                        checkboxSelection={true}
                        keepNonExistentRowsSelected={true}
                        localeText={localeText}
                        selectionModel={selectedIds}
                        rowSelectionModel={selectedIds}
                        onRowSelectionModelChange={setSelectedIds}
                        onSelectionModelChange={setSelectedIds}
                        sortModel={sortModel}
                        deps={lastUpdate}
                        query={query}
                        relatedLoading={syncActive}
                        disableListBorder={false}
                        limitHeightForMobile={'calc(100vh - 300px)'}
                        autorefresh
                        showRoleChips={false}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default UserListTab;